/**
 * Landing v3 — the study content inside the live feature cards.
 *
 * Medical, because that is who studies with Flashka most, and in the page's
 * language (user 2026-09-27: a visitor on `/it` studies an Italian card). Each
 * language is its own module in `samples/`, loaded with the first media that
 * needs it, so a visitor downloads one language, never eight, and none of it
 * rides in the landing's entry chunk. Each block is DATA for a real app
 * component (the card renderer, the quiz, the mind-map layout, the occlusion
 * editor, the transcript), shaped exactly as the app would store it, so the
 * components read it the way they read a learner's own material.
 *
 * What does not change with the language (card shapes, the skull's geometry,
 * the exam plan) lives here.
 */
import type { TargetSegment } from '@flashka/shared/api';
import type { MindmapData } from '@flashka/shared/mindmap';
import type { MCQuizQuestion } from '@flashka/shared/quiz/logic';

import { use } from 'react';
import { useTranslation } from 'react-i18next';

import type { SkullBone } from './skull-labels';

// ── The shape every language fills ───────────────────────────────────────────

export type SampleCard = { front: string; back: string };

/** The card-type switch of Figma 60:21855, in its order. */
export const FLASHCARD_TABS = ['basic', 'mcq', 'cloze', 'terms'] as const;
export type FlashcardTab = (typeof FLASHCARD_TABS)[number];

/** A generated MCQ plus the slot its correct answer is shuffled into (the quiz
 *  keeps that per question so a re-render never moves the answer). */
export type SampleQuizQuestion = MCQuizQuestion & { correctIndex: number };

export type LandingSamples = {
  /**
   * One card per shape. The app detects the shape from the text itself
   * (`@flashka/shared/flashcards`): `A) … B) …` with the answer on the back is
   * an MCQ, `_____` blanks with comma-separated answers are a cloze — so every
   * language writes them the way a learner (or the AI) writes them, not
   * tagged, and a single `A.`–`F.` or `A)`–`F)` anywhere else would turn a
   * card into an MCQ (`samples.test.ts` pins each shape).
   */
  flashcards: Record<FlashcardTab, SampleCard>;
  /** Three cards to rate; the row cycles through them. */
  srCards: SampleCard[];
  quiz: SampleQuizQuestion[];
  /** A flat parent-linked tree: the layout engine places it (two-sided tidy
   *  tree, branch hues), exactly as for a generated map. Same ids and parents
   *  in every language. */
  mindmap: MindmapData;
  lecture: {
    title: string;
    /** Paragraphs as the transcriber merges them: readable chunks with the
     *  second they start and end at, so the transcript can follow a playhead.
     *  Same timings in every language. */
    transcript: TargetSegment[];
  };
  /** Seeds the editor as a generated AI note would (markdown → the editor's
   *  document), then the reader can type in it. */
  notesMarkdown: string;
  /** The skull's bone labels, drawn into the image in this language. */
  skullLabels: Record<SkullBone, string>;
  /**
   * What Prof. Ka says in the oral exam's card, in the order he says it: his
   * greeting, the first question, then after each answer his reaction and the
   * next question (`r1` right, `r2` wrong — he explains — `r3` right), then
   * his line while he grades. The same brain-lobes exam in every language.
   */
  oralExam: Record<'greet' | 'q1' | 'r1' | 'r2' | 'r3' | 'grading', string>;
  /** The case-study card: a generated case, the learner's answer, and what
   *  the grade says they nailed and missed. */
  caseStudy: {
    title: string;
    scenario: string;
    task: string;
    answer: string;
    nailed: string[];
    missed: string[];
  };
};

// ── Loading the page's language ──────────────────────────────────────────────

export const SAMPLE_LANGS = [
  'en',
  'it',
  'es',
  'fr',
  'de',
  'sv',
  'pl',
  'he',
  'ca',
] as const;
export type SampleLang = (typeof SAMPLE_LANGS)[number];

const LOADERS: Record<SampleLang, () => Promise<{ default: LandingSamples }>> = {
  en: () => import('./samples/en'),
  it: () => import('./samples/it'),
  es: () => import('./samples/es'),
  fr: () => import('./samples/fr'),
  de: () => import('./samples/de'),
  sv: () => import('./samples/sv'),
  pl: () => import('./samples/pl'),
  he: () => import('./samples/he'),
  ca: () => import('./samples/ca'),
};

function sampleLang(language: string | undefined): SampleLang {
  const base = (language ?? 'en').split('-')[0] as SampleLang;
  return SAMPLE_LANGS.includes(base) ? base : 'en';
}

const pending = new Map<SampleLang, Promise<LandingSamples>>();

/** The samples in `language` (English for any other), fetched once. Called
 *  ahead of need with the media chunks, so a card rarely waits for its text. */
export function loadLandingSamples(
  language: string | undefined,
): Promise<LandingSamples> {
  const lang = sampleLang(language);
  let promise = pending.get(lang);
  if (!promise) {
    promise = LOADERS[lang]().then((m) => m.default);
    // A failed fetch (offline, deploy skew) is retried by the next mount.
    promise.catch(() => pending.delete(lang));
    pending.set(lang, promise);
  }
  return promise;
}

/** The samples in the page's language. Suspends while they load — every media
 *  renders inside `FeatureMedia`'s Suspense, which shows the empty slot. */
export function useLandingSamples(): LandingSamples {
  const { i18n } = useTranslation();
  return use(loadLandingSamples(i18n.language));
}

// ── Language-independent ─────────────────────────────────────────────────────

/**
 * Lateral view of the skull — LadyofHats (Mariana Ruiz Villarreal), released
 * into the public domain on Wikimedia Commons (`Human skull side simplified
 * (bones).svg`), with its white plate and its English bone labels removed: the
 * labels are drawn back in the page's language (`skull-labels`). Kept at its
 * natural size, which is the space the labels' boxes are measured in.
 */
export const SKULL_IMAGE = {
  url: '/assets/landing/occlusion/skull-bones-blank.svg',
  width: 740,
  height: 576,
} as const;

/** An exam three and a half weeks out, studied Monday to Friday, from a deck
 *  of 120 cards — dates are computed from the real today at render. */
export const PLANNER_SAMPLE = {
  daysToExam: 24,
  deckSize: 120,
  /** Monday-first, 0 = Mon … 6 = Sun. */
  studyWeekdays: [0, 1, 2, 3, 4] as readonly number[],
} as const;

/** The transcript's length, read from any language (the timings are shared). */
export function lectureDurationSec(transcript: TargetSegment[]): number {
  return transcript[transcript.length - 1]?.end ?? 0;
}
